<template>
  <bulk-update-wrapper
    :saving="saving"
    title="Update Destination"
    :button-label="props.buttonLabel"
    :has-changes="hasChanges"
    :show-hint="showHint"
    @save="save"
    @dismiss="dismiss"
  >
    <new-location-picker
      :value="destinationId"
      data-test="bulk-update-destination"
      label="Destination"
      required
      @input="updateDestination"
    />
    <template #error-snackbar>
      <vs-snackbar :value="error" type="error" sticky @input="clearSnackbar">
        Failed to update destination
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import BulkUpdateWrapper from '../common/BulkUpdateWrapper.vue'
import NewLocationPicker from '@/components/locations/NewLocationPicker.vue'
import { computed, ref, onMounted } from 'vue'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { bulkUpdateDestination } from '@/api/jobs'

const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', { message, updatedJobs }: BulkUpdateResponse)
}>()

const props = defineProps<{
  buttonLabel: string
  selectedJobs: JobJson[]
}>()

const error = ref(false)
const saving = ref(false)
const destinationId = ref<number | undefined>()
let initialDestinationId: number | undefined

onMounted(() => {
  const selectedDestinationIds: Set<number> = new Set(
    props.selectedJobs.map((job) => job.destination_id),
  )
  if (selectedDestinationIds.size === 1) {
    initialDestinationId = [...selectedDestinationIds][0]
  }
  destinationId.value = initialDestinationId
})

const hasChanges = computed<boolean>(() => {
  return destinationId.value !== initialDestinationId
})
const showHint = computed<boolean>(() => {
  return !destinationId.value && initialDestinationId === undefined
})

const updateDestination = (input: number | number[]) => {
  if (!(input instanceof Array)) {
    destinationId.value = input
  }
}

const clearSnackbar = () => (error.value = false)
const dismiss = () => emits('dismiss')

const save = async () => {
  if (destinationId.value === undefined) {
    error.value = true
    return
  }

  saving.value = true
  try {
    await bulkUpdateDestination(
      props.selectedJobs.map((job) => job.number),
      destinationId.value,
    )
    const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
      Object.assign(job, { destination_id: destinationId.value }),
    )
    emits('updated', { message: 'Destination updated', updatedJobs })
  } catch (err) {
    error.value = true
  } finally {
    saving.value = false
  }
}
</script>
